import { useEffect, useRef, useState } from 'react';

interface TicketProps {
  onImageClick: (imageUrl: string) => void;
  onVenueImageClick: (imageUrl: string) => void;
}

export default function Ticket({ onImageClick, onVenueImageClick }: TicketProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Guard against environments where IntersectionObserver is not available
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const venueImageUrl = 'https://www.irinomiyu-uuu.com/img/goods02_b97beb0ddd7.png';

  const openVenueModal = (src: string) => {
    if (window.innerWidth < 768) return;
    onVenueImageClick(src);
  };

  const ticketTypes = [
    {
      name: 'VIPチケット',
      type: 'スタンディング',
      price: '19,800円（税込）',
      features: [
        'ステージ前方エリアでご覧いただけます',
        '特典グッズ付き：\nスタッフTシャツ(会場毎にデザイン共通でカラーが異なります)\nニットバッグ(共通デザイン)\nVIPパスステッカー(会場毎の限定デザイン)',
        'CD予約先行（CMS早期予約特典）とオフィシャル1次のみの販売'
      ],
      featuresMobile: [
        {
          lines: ['ステージ前方エリアでご覧いただけます']
        },
        {
          lines: [
            '特典グッズ付き：',
            'スタッフTシャツ(会場毎にデザイン共通でカラーが異なります)',
            'ニットバッグ(共通デザイン)',
            'VIPパスステッカー(会場毎の限定デザイン)'
          ]
        },
        {
          lines: [
            'CD予約先行（CMS早期予約特典）と',
            'オフィシャル1次のみの販売'
          ]
        }
      ],
      color: '#fc457e',
      isVip: true
    },
    {
      name: 'グッズ付き指定席',
      type: '',
      price: '15,000円（税込）',
      features: [
        '特典グッズ付き：ニットバック（非売品）',
        'CD予約先行（CMS早期予約特典）とオフィシャル1次のみの販売'
      ],
      featuresMobile: [
        {
          lines: ['特典グッズ付き：ニットバック（非売品）']
        },
        {
          lines: [
            'CD予約先行（CMS早期予約特典）と',
            'オフィシャル1次のみの販売'
          ]
        }
      ],
      color: '#44c4ff'
    },
    {
      name: 'グッズ付きチケット',
      type: 'スタンディング',
      price: '14,000円（税込）',
      features: [
        '特典グッズ付き：ニットバック（非売品）',
        'CD予約先行（CMS早期予約特典）とオフィシャル1次のみの販売'
      ],
      featuresMobile: [
        {
          lines: ['特典グッズ付き：ニットバック（非売品）']
        },
        {
          lines: [
            'CD予約先行（CMS早期予約特典）と',
            'オフィシャル1次のみの販売'
          ]
        }
      ],
      color: '#44c4ff'
    },
    {
      name: '指定席',
      type: '',
      price: '10,000円（税込）',
      features: [],
      featuresMobile: [],
      color: '#44c4ff'
    },
    {
      name: 'スタンディング',
      type: '',
      price: '9,000円（税込）',
      features: [],
      featuresMobile: [],
      color: '#44c4ff'
    }
  ];

  const venues = [
    {
      name: 'Kanadevia Hall',
      image: 'https://www.irinomiyu-uuu.com/img/00e04982039c007b53a835080deaba54.png'
    },
    {
      name: 'Zepp Osaka Bayside',
      image: 'https://www.irinomiyu-uuu.com/img/a8fe75ba9440fbedf653ce097d8815f8.png'
    },
    {
      name: 'Zepp Fukuoka',
      image: 'https://www.irinomiyu-uuu.com/img/4ebe5347239505e8e60208afefca0e09.png'
    },
    {
      name: 'Zepp Haneda (TOKYO)',
      image: 'https://www.irinomiyu-uuu.com/img/b4dbe8ebd58b13943ed3010c1985b7f7.png'
    }
  ];

  return (
    <>
      <section
        ref={sectionRef}
        id="ticket"
        className={`py-20 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#fcb122] px-8 md:px-12 py-3 md:py-4 shadow-[0_0_40px_#fcb122]">
              <h2 className="text-3xl md:text-5xl text-[#0a0a0a] font-bold">TICKET</h2>
            </div>
          </div>

          <div className="grid gap-6 mb-8">
            {ticketTypes.map((ticket, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-4 p-4 md:p-8 hover:scale-105 transition-all duration-300 hover:shadow-[0_0_40px_var(--glow-color)]"
                style={{
                  borderColor: ticket.color,
                  boxShadow: `0 0 30px ${ticket.color}60`,
                  '--glow-color': ticket.color
                } as React.CSSProperties}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
                      {ticket.isVip && <span className="text-xl md:text-2xl">👑</span>}
                      <h3 className="text-base md:text-2xl text-white font-bold break-words">
                        {ticket.name}
                        {ticket.type && (
                          <span className="block md:inline text-xs md:text-base text-white/70 md:ml-2 break-words">
                            （{ticket.type}）
                          </span>
                        )}
                      </h3>
                    </div>
                  </div>
                  <div
                    className="px-4 md:px-6 py-2 md:py-3 border-2 text-center font-bold shrink-0"
                    style={{
                      backgroundColor: ticket.color,
                      borderColor: ticket.color,
                      color: '#0a0a0a',
                      boxShadow: `0 0 20px ${ticket.color}`
                    }}
                  >
                    <span className="text-sm md:text-xl whitespace-nowrap">{ticket.price}</span>
                  </div>
                </div>

                {ticket.features.length > 0 && (
                  <>
                    {/* PC版 */}
                    <div className="hidden md:block space-y-2 text-xs md:text-sm text-white/90">
                      {ticket.features.map((feature, idx) => (
                        <p key={idx} className="flex items-start gap-2">
                          <span style={{ color: ticket.color }} className="font-dotgothic shrink-0">
                            ▷
                          </span>
                          <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{feature}</span>
                        </p>
                      ))}
                    </div>

                    {/* スマホ版 */}
                    <div className="md:hidden space-y-2 text-xs text-white/90">
                      {ticket.featuresMobile.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span style={{ color: ticket.color }} className="font-dotgothic shrink-0">
                            ▷
                          </span>
                          <div className="break-words overflow-wrap-anywhere min-w-0 flex-1">
                            {feature.lines.map((line, lineIdx) => (
                              <p key={lineIdx} className="break-words">{line}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* 注意事項 */}
          <div className="space-y-4 mb-16">
            <div className="bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4">
              <p className="text-xs md:text-sm text-white/70 break-words">
                ※グッズのお渡しは当日会場のみとなります。ご来場いただけなかった場合の後日お渡しや個別での発送対応はお受けできません。あらかじめご了承ください。
              </p>
            </div>
            <div className="bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4">
              {/* PC版 */}
              <p className="hidden md:block text-xs md:text-sm text-white/70 break-words">
                <strong className="text-[#44c4ff]">枚数制限：</strong>お一人様1公演につき4枚まで（複数公演申込可）
              </p>
              {/* スマホ版 */}
              <p className="md:hidden text-xs text-white/70 break-words">
                <strong className="text-[#44c4ff]">枚数制限：</strong>お一人様1公演につき4枚まで<br />
                （複数公演申込可）
              </p>
            </div>
          </div>

          {/* 会場図面 */}
          <div className="mb-16">
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {venues.map((venue, index) => (
                <li key={index} className="border-4 border-[#44c4ff] bg-white w-full">
                  <div className="w-full flex flex-col items-center justify-start p-4">
                    <div className="h-12 flex items-center justify-center w-full">
                      <h3 className="text-sm md:text-base text-[#0a0a0a] font-bold text-center break-words leading-tight">{venue.name}</h3>
                    </div>
                    <p className="text-xs text-[#0a0a0a] text-center mb-4">【スタンディング図】</p>
                    <div className="w-full flex items-start justify-center">
                      <img
                        src={venue.image}
                        alt={`${venue.name}の会場図`}
                        className="w-full h-auto object-contain object-top md:cursor-pointer md:hover:opacity-80 transition-opacity duration-200"
                        onClick={() => openVenueModal(venue.image)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4 mt-6">
              <p className="text-xs md:text-sm text-white/70 break-words">
                ※2025年12月19日（金）時点での座席レイアウト・イメージとなります。<br />
                ※ステージプラン及び、各席種の販売状況次第で、実際の座席レイアウトが予告なく変更となる場合がございます。<br />
                ※座席レイアウト変更に伴う、返金対応・振替対応等はいたしかねます。予めご了承の上でご購入ください。
              </p>
            </div>
          </div>

          {/* 来場者特典 */}
          <div className="bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-4 border-[#1ef99d] p-6 md:p-8 shadow-[0_0_40px_#1ef99d60]">
            <div className="text-center mb-8">
              <h3 className="text-xl md:text-2xl text-[#1ef99d] mb-4 flex items-center justify-center gap-3 font-bold">
                <span className="font-dotgothic">☆</span>
                <span>来場者特典</span>
                <span className="font-dotgothic">☆</span>
              </h3>
            </div>

            <div className="mb-8 flex justify-center">
              <img
                src={venueImageUrl}
                alt="来場者特典グッズ画像"
                className="w-full max-w-md md:max-w-lg h-auto rounded-lg md:cursor-pointer md:hover:opacity-90 transition-opacity duration-300 md:hover:shadow-[0_0_20px_#1ef99d]"
                onClick={() => {
                  if (window.innerWidth >= 768) {
                    onImageClick(venueImageUrl);
                  }
                }}
              />
            </div>

            <div className="bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4">
              <p className="text-xs md:text-sm text-white/70 break-words">
                ※各会場毎に異なるデザインになります。<br />
                ※黒抜き部分は写真が入ります。写真の絵柄は当日のお楽しみ！<br />
                ※来場者特典のお渡しは当日会場のみとなります。ご来場いただけなかった場合の後日お渡しや個別での発送対応はお受けできません。あらかじめご了承ください。
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
